import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Palette, Check, Sliders, RotateCcw } from "lucide-react";

import {
  THEMES,
  CUSTOMIZABLE_TOKENS,
  type CustomToken,
  type CustomMap,
} from "./themeConstants";
import { readPreset, writePreset, readCustom, writeCustom } from "./themeStorage";
import { applyPreset, applyCustom } from "./themeApply";
import { saveTheme } from "./themeApi";
import { toast } from "sonner";

export function ThemeSwitcher({ compact = false }: { compact?: boolean }) {
  const [current, setCurrent] = useState<string>("");
  const [customOpen, setCustomOpen] = useState(false);

  useEffect(() => {
    setCurrent(readPreset());
  }, []);

  function pick(id: string) {
    setCurrent(id);
    writePreset(id);
    applyPreset(id);
    // Persist lên backend để các máy khác / visitor public cũng thấy theme này.
    void saveTheme({ preset: id, custom: readCustom() }).then((ok) => {
      if (!ok) {
        try { toast.error("Không lưu được theme lên server. Đã áp tạm cho máy này."); } catch {}
      }
    });
  }

  const active = THEMES.find((t) => t.id === current) ?? THEMES[0];

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {compact ? (
            <Button variant="ghost" size="sm" className="w-full justify-start gap-2 h-8">
              <span
                aria-hidden
                className="h-3.5 w-3.5 rounded-full border border-border"
                style={{ background: active.swatch, boxShadow: `0 0 8px ${active.swatch}` }}
              />
              <Palette className="h-3.5 w-3.5" />
              <span className="flex-1 text-left">Màu sắc</span>
            </Button>
          ) : (
            <Button variant="outline" size="sm" className="gap-2 h-8">
              <span
                aria-hidden
                className="h-3.5 w-3.5 rounded-full border border-border"
                style={{ background: active.swatch, boxShadow: `0 0 10px ${active.swatch}` }}
              />
              <Palette className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Theme</span>
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs">Bộ màu preset</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <div className="max-h-72 overflow-y-auto">
            {THEMES.map((t) => (
              <DropdownMenuItem key={t.id || "default"} onClick={() => pick(t.id)} className="gap-2">
                <span
                  aria-hidden
                  className="h-3.5 w-3.5 rounded-full border border-border"
                  style={{ background: t.swatch, boxShadow: `0 0 8px ${t.swatch}` }}
                />
                <span className="flex-1">{t.label}</span>
                {current === t.id && <Check className="h-3.5 w-3.5 text-primary" />}
              </DropdownMenuItem>
            ))}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setCustomOpen(true); }} className="gap-2">
            <Sliders className="h-3.5 w-3.5" />
            <span className="flex-1">Tùy chỉnh chi tiết...</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CustomThemeDialog open={customOpen} onOpenChange={setCustomOpen} />
    </>
  );
}

function CustomThemeDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [values, setValues] = useState<CustomMap>({});

  useEffect(() => {
    if (open) setValues(readCustom());
  }, [open]);

  function update(key: CustomToken, val: string) {
    const next = { ...values, [key]: val };
    setValues(next);
    writeCustom(next);
    applyCustom(next);
    void saveTheme({ preset: readPreset(), custom: next });
  }

  function reset(key: CustomToken) {
    const next = { ...values };
    delete next[key];
    setValues(next);
    writeCustom(next);
    applyCustom(next);
    void saveTheme({ preset: readPreset(), custom: next });
  }

  function resetAll() {
    setValues({});
    writeCustom({});
    applyCustom({});
    void saveTheme({ preset: readPreset(), custom: {} });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sliders className="h-4 w-4" /> Tùy chỉnh màu cả site
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground -mt-1">
          Các màu áp dụng cho cả trang công khai và admin. Bỏ trống một ô để dùng lại màu mặc định
          của bộ preset đang chọn.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          {CUSTOMIZABLE_TOKENS.map((t) => {
            const current = values[t.key] ?? "";
            const display = current || t.default;
            return (
              <div key={t.key} className="flex items-center gap-2 rounded-md border border-border/60 p-2">
                <input
                  type="color"
                  value={display.startsWith("#") ? display : t.default}
                  onChange={(e) => update(t.key, e.target.value)}
                  className="h-8 w-10 rounded cursor-pointer border border-border/60 bg-transparent"
                  aria-label={t.label}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{t.label}</div>
                  <input
                    type="text"
                    value={current}
                    onChange={(e) => update(t.key, e.target.value)}
                    placeholder={t.default}
                    className="w-full bg-transparent text-[11px] text-muted-foreground outline-none"
                  />
                </div>
                {current && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 shrink-0"
                    onClick={() => reset(t.key)}
                    title="Khôi phục mặc định"
                  >
                    <RotateCcw className="h-3 w-3" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={resetAll}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Khôi phục tất cả
          </Button>
          <Button onClick={() => onOpenChange(false)}>Xong</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
